// Which rows the drill-down table is built from, and which of its rows is the
// one you selected.
//
// The table is scoped by the levels STRICTLY ABOVE the drill level. A selection
// at the drill level itself, or below it, only highlights a row -- it does not
// filter the list.
//
// The alternative, filtering by the whole scope, is what the table used to do,
// and it made the table destroy itself: you sorted 20 sites, clicked the one you
// cared about, and the list collapsed to that single row. Getting it back meant
// clearing the chip and losing your place. It also removed the one comparison
// the page otherwise lacks -- the pinned rows give you division and company
// averages, but not your peers -- and left the green/red banding, which is a
// company-wide percentile, ranking a single row against nothing.
//
// "Strictly above" rather than "everything but the drill level" is what makes
// moving back UP a level behave: from a doctor at a site, switching to Site
// level releases both the doctor and the site and shows the division's sites.
// Keeping the doctor's filter there would have shown one site and called it a
// list.

import type { DrillLevel, ScopeFilters } from "../types/filters";

const ORDER: DrillLevel[] = ["country", "group", "division", "site", "dvm"];

const NONE: ScopeFilters = {
  countryCode: null,
  groupName: null,
  divisionName: null,
  locationId: null,
  resourceId: null,
};

/**
 * The scope a peer list at `drillLevel` is drawn from: every level above it,
 * nothing at or below.
 *
 * resourceId is always null. A doctor is the deepest level, so nothing is ever
 * grouped beneath one -- and site-week rollup rows carry no resource_id at all,
 * so a resource filter could not be satisfied from them anyway.
 */
export function peerScope(scope: ScopeFilters, drillLevel: DrillLevel): ScopeFilters {
  const i = ORDER.indexOf(drillLevel);
  if (i <= 0) return NONE;
  return {
    countryCode: scope.countryCode,
    groupName: i > 1 ? scope.groupName : null,
    divisionName: i > 2 ? scope.divisionName : null,
    locationId: i > 3 ? scope.locationId : null,
    resourceId: null,
  };
}

/** The id of the row to highlight, or null when nothing is selected at this level. */
export function selectedDrillId(scope: ScopeFilters, drillLevel: DrillLevel): string | null {
  switch (drillLevel) {
    case "country":
      return scope.countryCode;
    case "group":
      return scope.groupName;
    case "division":
      return scope.divisionName;
    case "site":
      return scope.locationId;
    case "dvm":
      return scope.resourceId;
  }
}

/** The level a click at `drillLevel` should open, or null at the deepest level. */
export function nextDrillLevel(drillLevel: DrillLevel): DrillLevel | null {
  return ORDER[ORDER.indexOf(drillLevel) + 1] ?? null;
}
